import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { EditorState, Plugin } from "prosemirror-state";
import { baseKeymap } from "prosemirror-commands";
import { keymap } from "prosemirror-keymap";
import { createSchema, RichTextEditorSchema } from "./schema";
import { buildKeymap } from "./keymap";
import {
	RichTextEditorToolbar,
	RichTextEditorToolbarFeature
} from "./Toolbar/RichTextEditorToolbar";
import {
	MentionRichTextEditorContentProps,
	RichTextEditorContent,
	RichTextEditorContentHandle,
	RichTextEditorElementModifiers
} from "./Content/RichTextEditorContent";
import {
	ProseMirrorRichTextEditorState,
	RichTextEditorState,
	RichTextEditorStateKind
} from "./types";
import { columnResizing, tableEditing } from "prosemirror-tables";
import { history } from "prosemirror-history";
import { gapCursor } from "prosemirror-gapcursor";
import styles from "./RichTextEditor.module.less";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { RichTextEditorToolProps } from "./tools/useProseMirrorRichTextEditorTool";
import { insertListPlugin } from "./plugins/insertListPlugin";
import { linkifyPlugin } from "./plugins/linkifyPlugin";
import { defaultFontMarksPlugin } from "./plugins/defaultFontMarksPlugin";
import { StyledTableView } from "./views/styledTableView";
import { cellMinWidth } from "./tableRendering";
export {
	useRichTextEditorRewriteTools,
	type RichTextEditorRewriteToolResponse,
	type SuccessfulRichTextEditorRewriteToolResponse,
	type RichTextEditorRewriteToolsResult,
	type RichTextEditorRewriteToolsService
} from "./tools/useRichTextEditorRewriteTools";

const {
	"Enter": _,
	...baseKeymapWithoutEnterBinding
} = baseKeymap;

const schema: RichTextEditorSchema = createSchema(
	[
		"doc",
		"paragraph",
		"blockquote",
		"horizontal_rule",
		"heading",
		"code_block",
		"text",
		"image",
		"hard_break",
		"orderedList",
		"bulletList",
		"listItem",
		"table",
		"table_row",
		"table_cell",
		"table_header",
		"divLike"
	],
	[
		"link",
		"em",
		"underline",
		"strikeThrough",
		"strong",
		"code",
		"fontSize",
		"fontFamily",
		"color",
		"mention",
	]
);

export function createInitialEditorValue(
	extraPlugins: readonly Plugin[] = []
): ProseMirrorRichTextEditorState {
	return {
		kind: RichTextEditorStateKind.ProseMirror,
		editorState: EditorState.create({
			schema,
			plugins: [
				insertListPlugin(schema),
				linkifyPlugin(schema),
				keymap(buildKeymap(schema)),
				keymap(baseKeymapWithoutEnterBinding),
				history(),
				gapCursor(),
				columnResizing({
					View: StyledTableView,
					cellMinWidth
				}),
				tableEditing({ allowTableNodeSelection: true }),
				defaultFontMarksPlugin,
				...extraPlugins
			]
		})
	};
}

export const initialEditorValue: RichTextEditorState = createInitialEditorValue();

export type RichTextEditorProps = {
	value: RichTextEditorState | undefined;
	onChange(newState: RichTextEditorState, isSignificant: boolean): void;
	onBlur?(newState: RichTextEditorState): void;
	placeholder?: string;
	isInvalid?: boolean;
	isDisabled?: boolean;
	areHashUrlsAllowed?: boolean;
	height?: "auto" | "fill" | "resizable";
	supportedFeatures?: readonly RichTextEditorToolbarFeature[];
	renderCustomTool?(props: RichTextEditorToolProps): ReactElement | null;
	elementModifiers?: RichTextEditorElementModifiers;
	trackingId: string;
}
	& MentionRichTextEditorContentProps;

export const RichTextEditor = (props: RichTextEditorProps): ReactElement => {
	const {
		value = initialEditorValue,
		onChange,
		onBlur,
		height,
		placeholder,
		isInvalid = false,
		isDisabled = false,
		areHashUrlsAllowed = false,
		supportedFeatures,
		renderCustomTool,
		elementModifiers,
		trackingId,
		...mentionInContentProps
	} = props;

	if (value.kind !== RichTextEditorStateKind.ProseMirror) {
		throw new Error("This component only works with prosemirror state");
	}

	const viewRef: RefObject<RichTextEditorContentHandle | null> = useRef<RichTextEditorContentHandle>(null);

	const focusHandler = useCallback(
		() => {
			viewRef.current
				?.focus();
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				{
					[styles.root_fillHeight]: height === "fill"
				}
			)}
		>
			<RichTextEditorToolbar
				onFocusInput={focusHandler}
				value={value}
				onChange={onChange}
				attachedOn="bottom"
				isInvalid={isInvalid}
				supportedFeatures={supportedFeatures}
				renderCustomTool={renderCustomTool}
				trackingId={generateTrackingId(trackingId, "__toolbar")}
				isDisabled={isDisabled}
				areHashUrlsAllowed={areHashUrlsAllowed}
			/>
			<div className={styles.viewWrapper}>
				<RichTextEditorContent
					{...mentionInContentProps}
					value={value}
					onChange={onChange}
					isInvalid={isInvalid}
					isDisabled={isDisabled}
					onBlur={onBlur}
					attachedOn={isDisabled ? undefined : "top"}
					height={height}
					ref={viewRef}
					placeholder={placeholder}
					trackingId={generateTrackingId(trackingId, "__content")}
					elementModifiers={elementModifiers}
				/>
			</div>
		</div>
	);
};
RichTextEditor.displayName = "RichTextEditor";

export type RichTextEditorIntermediateStateHandlers = {
	intermediateState: RichTextEditorState;
	setIntermediateState: Dispatch<SetStateAction<RichTextEditorState>>;
	getHtmlThatRepresentsTheState(): string;
	setHtmlThatRepresentsTheState(html: string): void;
};

export function useRichTextEditorIntermediateState(
	initialState: RichTextEditorState = initialEditorValue
): RichTextEditorIntermediateStateHandlers {
	const [intermediateState, setIntermediateState] = useState<RichTextEditorState>(initialState);
	const htmlThatRepresentsTheStateRef: RefObject<string> = useRef<string>("");

	const result = useMemo(
		(): RichTextEditorIntermediateStateHandlers => {
			return {
				intermediateState,
				setIntermediateState,
				getHtmlThatRepresentsTheState(): string {
					return htmlThatRepresentsTheStateRef.current;
				},
				setHtmlThatRepresentsTheState(html: string): void {
					htmlThatRepresentsTheStateRef.current = html;
				}
			};
		},
		[intermediateState]
	);

	return result;
}